import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ConfirmDialog } from "@/components/ui-system";
import { Mail, RotateCcw } from "lucide-react";

const FIELDS = [
  { key: "subject", label: "Subject", max: 200 },
  { key: "intro", label: "Message", rows: 6, max: 5000, hint: "Shown after the greeting. Leave a blank line between paragraphs." },
  { key: "closing", label: "Closing note", rows: 3, max: 5000, hint: "Shown before the sign-off. Optional." },
];

/** Subject / Message / Closing note, starting from the selected announcement type's backend defaults. */
export default function MessageEditor({ message, setMessage, defaults, showErrors, disabled }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const changed = FIELDS.some((f) => message[f.key] !== defaults[f.key]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-lg flex items-center gap-2"><Mail className="w-5 h-5 text-gray-500" />Email Message</CardTitle>
          <Button type="button" variant="ghost" size="sm" disabled={disabled || !changed}
            onClick={() => setConfirmOpen(true)} data-testid="ca-reset-message">
            <RotateCcw className="w-4 h-4 mr-1" />Reset to default
          </Button>
        </div>
        <p className="text-xs text-gray-500">The greeting (Dear &lt;name&gt;,) and the sign-off are added for each recipient.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {FIELDS.map((f) => {
          const id = `ca-message-${f.key}`;
          const missing = showErrors && f.key !== "closing" && !message[f.key].trim();
          const props = {
            id, value: message[f.key], maxLength: f.max, disabled, "aria-invalid": missing,
            className: `mt-1 ${missing ? "border-red-500" : ""}`,
            onChange: (e) => setMessage({ ...message, [f.key]: e.target.value }),
            "data-testid": id,
          };
          return (
            <div key={f.key}>
              <Label htmlFor={id} className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                {f.label}{f.key !== "closing" && <span className="text-red-600" aria-hidden="true"> *</span>}
              </Label>
              {f.rows ? <Textarea rows={f.rows} {...props} /> : <Input {...props} />}
              {missing ? <p className="text-xs text-red-600 mt-1">{f.label} is required</p>
                : f.hint && <p className="text-xs text-gray-400 mt-1">{f.hint}</p>}
            </div>
          );
        })}
      </CardContent>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Reset the email message?"
        description="Your subject, message and closing note will be replaced with this announcement type's default wording."
        confirmLabel="Reset"
        onConfirm={() => { setMessage({ ...defaults }); setConfirmOpen(false); }}
      />
    </Card>
  );
}
